import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    ContentChild,
    DestroyRef,
    EventEmitter,
    inject,
    Input,
    Output,
    TemplateRef,
} from '@angular/core';
import { translate, TranslocoService } from '@jsverse/transloco';
import { selectTranslate } from '@libs/vc-core-lib';
import Highcharts, {
    Chart,
    SeriesAreasplineOptions,
    SeriesClickEventObject,
    XAxisOptions,
    YAxisOptions,
} from 'highcharts';
import { HighchartsChartComponent } from 'highcharts-angular';
import { isEmpty } from 'lodash-es';
import { ChartWrapperComponent } from '../../chart-wrapper/chart-wrapper.component';

export interface IEventsFindingData {
    eventsData: number[];
    findingsData: number[];
    categories: string[];
}

@Component({
    selector: 'vc-events-findings-chart',
    imports: [CommonModule, HighchartsChartComponent, ChartWrapperComponent],
    templateUrl: './events-findings-chart.component.html',
    styleUrl: './events-findings-chart.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EventsFindingsChartComponent {
    private readonly _transloco: TranslocoService = inject(TranslocoService);
    private readonly _destroyRef = inject(DestroyRef);
    highcharts: typeof Highcharts = Highcharts;
    eventsTitle: string = translate('LANDING.CHART.TITLE.EVENTS');
    findingsTitle: string = translate('LANDING.CHART.TITLE.FINDINGS');

    private chartInstance?: Chart;

    @ContentChild('chartActionsRightSide')
    chartActionsTemplate!: TemplateRef<unknown>;

    @Output()
    seriesClick = new EventEmitter<SeriesClickEventObject>();

    @Input()
    loading = false;

    @Input()
    title: string = translate('LANDING.CHART.TITLE.EVENTS/FINDINGS');

    @Input()
    contentHeight: string = '340px';

    @Input()
    set xAxisTitle(value: string) {
        this._xAxisTitle = value;
        if (this.chartOptions.xAxis) {
            (this.chartOptions.xAxis as XAxisOptions).title = { text: value };
        }
        this._updateChart();
    }
    get xAxisTitle(): string {
        return this._xAxisTitle;
    }
    private _xAxisTitle: string = translate('CORE.HOUR');

    @Input()
    set categoryFormatter(value: ((value: string) => string) | undefined) {
        this._categoryFormatter = value;
        if (value && this.chartOptions.xAxis) {
            (this.chartOptions.xAxis as XAxisOptions).labels = {
                formatter: (context) => value(context.value as string),
            };
        }
        this._updateChart();
    }
    get categoryFormatter(): ((value: string) => string) | undefined {
        return this._categoryFormatter;
    }
    private _categoryFormatter?: (value: string) => string;

    @Input()
    set data(value: IEventsFindingData | null) {
        if (value) {
            this._updateChartData(value);
        }
        this._data = value;
    }
    get data(): IEventsFindingData | null {
        return this._data;
    }
    private _data: IEventsFindingData | null = null;

    constructor() {
        selectTranslate(this._transloco, () => this._rerenderChartTitles(), this._destroyRef);
    }

    private _rerenderChartTitles(): void {
        this.eventsTitle = translate('LANDING.CHART.TITLE.EVENTS');
        this.findingsTitle = translate('LANDING.CHART.TITLE.FINDINGS');

        const xAxis = this.chartOptions.xAxis as XAxisOptions;
        xAxis.title = { text: this.xAxisTitle };

        const yAxes = this.chartOptions.yAxis as YAxisOptions[];
        yAxes[0].title = {
            text: `<span class="accent-text">${this.eventsTitle}</span>`,
            useHTML: true,
        };
        yAxes[1].title = {
            text: `<span class="accent-text">${this.findingsTitle}</span>`,
            useHTML: true,
        };

        const series0 = this.chartOptions.series?.[0] as SeriesAreasplineOptions;
        const series1 = this.chartOptions.series?.[1] as SeriesAreasplineOptions;
        series0.name = this.eventsTitle;
        series1.name = this.findingsTitle;

        this._updateChart();
    }

    chartOptions: Highcharts.Options = {
        chart: {
            type: 'areaspline',
            backgroundColor: 'transparent',
            borderWidth: 0,
            spacingTop: 22,
        },
        title: {
            text: '',
        },
        credits: {
            enabled: false,
        },
        tooltip: {
            shared: true,
            headerFormat: '',
        },
        legend: {
            enabled: false,
        },
        xAxis: {
            categories: [],
            title: {
                text: translate('CORE.HOUR'),
            },
        },
        yAxis: [
            {
                title: {
                    text: `<span class="accent-text">${this.eventsTitle}</span>`,
                    useHTML: true,
                },
                labels: {
                    format: '{value:,.0f}',
                },
                min: 0,
                max: 5,
            },
            {
                title: {
                    text: `<span class="accent-text">${this.findingsTitle}</span>`,
                    useHTML: true,
                },
                labels: {
                    format: '{value:,.0f}',
                },
                opposite: true,
                min: 0,
                max: 5,
            },
        ],
        plotOptions: {
            series: {
                stickyTracking: false,
                events: {
                    click: (event: SeriesClickEventObject) => {
                        this._onSeriesClick(event);
                    },
                },
            },
        },
        series: [
            {
                marker: {
                    enabled: false,
                },
                data: [],
                name: translate('LANDING.CHART.TITLE.EVENTS'),
                color: 'var(--primary-200)',
                yAxis: 0,
                type: 'areaspline',
            },
            {
                marker: {
                    enabled: false,
                },
                data: [],
                name: translate('LANDING.CHART.TITLE.FINDINGS'),
                color: 'var(--primary-400)',
                yAxis: 1,
                type: 'areaspline',
            },
        ],
    };

    private _updateChartData(data: IEventsFindingData): void {
        const xAxis = this.chartOptions.xAxis as XAxisOptions;
        const series0 = this.chartOptions.series?.[0] as SeriesAreasplineOptions;
        const series1 = this.chartOptions.series?.[1] as SeriesAreasplineOptions;

        xAxis.categories = data.categories;
        series0.data = data.eventsData;
        series1.data = data.findingsData;

        const yAxes = this.chartOptions.yAxis as YAxisOptions[];
        yAxes[0].max = !isEmpty(data.eventsData) ? Math.max(...data.eventsData) : 5;
        yAxes[1].max = !isEmpty(data.findingsData) ? Math.max(...data.findingsData) : 5;
        this._updateChart();
    }

    onChartInstance(chart: Chart): void {
        this.chartInstance = chart;
        this._updateChart();
    }

    private _updateChart(): void {
        if (this.loading || !this.chartInstance?.renderer) {
            return;
        }

        this.chartInstance.update(this.chartOptions, true, true);
    }

    private _onSeriesClick(event: SeriesClickEventObject): void {
        this.seriesClick.emit(event);
    }
}
